import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Clock, Coins, User, MapPin, Users, ArrowLeft, Star, Calendar } from "lucide-react";
import { get } from "@/lib/api";
import { CATEGORY_LABELS, formatDate } from "@/lib/data";

export default function SessionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: s, isLoading } = useQuery({ queryKey: ["session", id], queryFn: () => get(`/sessions/${id}`) });

  if (isLoading || !s) return <div className="mx-auto max-w-4xl px-5 py-16 text-[#5C5856]">Yükleniyor…</div>;
  const spotsLeft = s.capacity - s.booked_count;
  const occupancy = Math.round((s.booked_count / s.capacity) * 100);

  return (
    <div className="mx-auto max-w-5xl px-5 py-8 lg:px-8">
      <Link to={`/studyo/${s.studio_id}`} className="mb-5 inline-flex items-center gap-1.5 text-sm text-[#5C5856] hover:text-[#2D2926]">
        <ArrowLeft className="h-4 w-4" /> {s.studio_name}
      </Link>

      <div className="grid gap-8 lg:grid-cols-[1fr_340px]">
        <div>
          <div className="overflow-hidden rounded-2xl">
            <img src={s.studio?.cover} alt="" className="h-64 w-full object-cover sm:h-80" />
          </div>
          <div className="mt-6 flex items-center gap-2">
            <span className="rounded-full bg-[#E8DED1] px-3 py-1 text-xs font-medium capitalize">{CATEGORY_LABELS[s.category]}</span>
            {s.is_last_minute && <span className="rounded-full bg-[#4A5D4E] px-3 py-1 text-xs font-medium text-white">Son Dakika</span>}
          </div>
          <h1 className="mt-3 font-display text-4xl font-semibold tracking-tight sm:text-5xl">{s.title}</h1>

          <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-2">
            {[
              { icon: Calendar, label: "Tarih", value: formatDate(s.date) },
              { icon: Clock, label: "Saat", value: `${s.start_time} – ${s.end_time}` },
              { icon: User, label: "Eğitmen", value: s.instructor },
              { icon: MapPin, label: "Konum", value: `${s.studio_name}, ${s.region}` },
            ].map((item) => (
              <div key={item.label} className="rounded-2xl border border-[#E5E0D8] bg-white p-4">
                <item.icon className="h-5 w-5 text-[#8C6A5D]" />
                <p className="mt-2 text-xs uppercase tracking-wide text-[#9C948B]">{item.label}</p>
                <p className="mt-0.5 font-medium">{item.value}</p>
              </div>
            ))}
          </div>

          <div className="mt-6 rounded-2xl border border-[#E5E0D8] bg-white p-5">
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-1.5 text-[#5C5856]"><Users className="h-4 w-4" /> Doluluk</span>
              <span className="font-medium">{s.booked_count}/{s.capacity} · %{occupancy}</span>
            </div>
            <div className="mt-3 h-2 overflow-hidden rounded-full bg-[#F0EAE0]">
              <div className="h-full rounded-full bg-[#8C6A5D]" style={{ width: `${occupancy}%` }} />
            </div>
            <p className="mt-2 text-sm text-[#5C5856]">{spotsLeft > 0 ? `${spotsLeft} kontenjan kaldı` : "Bu seans dolu"}</p>
          </div>
        </div>

        <aside className="h-fit lg:sticky lg:top-24">
          <div className="rounded-2xl border border-[#E5E0D8] bg-white p-6">
            {s.studio && (
              <div className="mb-4 flex items-center gap-1.5 text-sm text-[#5C5856]">
                <Star className="h-4 w-4 fill-[#8C6A5D] text-[#8C6A5D]" /> {s.studio.rating} · {s.studio.review_count} değerlendirme
              </div>
            )}
            <div className="flex items-baseline gap-2">
              {s.original_price && <span className="text-xl text-[#B0A79A] line-through">{s.original_price}</span>}
              <span className="font-display text-4xl font-semibold text-[#8C6A5D]">{s.credit_price}</span>
              <span className="text-[#5C5856]">kredi</span>
            </div>
            <button
              data-testid="go-to-booking-button"
              disabled={spotsLeft <= 0}
              onClick={() => navigate(`/rezervasyon/${s.id}`)}
              className="mt-6 w-full rounded-full bg-[#8C6A5D] py-3.5 font-medium text-white transition-colors hover:bg-[#705448] disabled:cursor-not-allowed disabled:bg-[#C9BFB1]"
            >
              {spotsLeft > 0 ? "Rezervasyon yap" : "Dolu"}
            </button>
            <p className="mt-3 text-center text-xs text-[#9C948B]">Ödeme kredinizden düşülecek</p>
          </div>
        </aside>
      </div>
    </div>
  );
}
